import { Fragment, useState, type ReactNode } from "react";
import Modal from "@/ui/Modal";
import { IconoAyuda } from "@/ui/Iconos";

/**
 * Botón «Ayuda» de una vista y su ventana de lectura.
 *
 * Aquí vive lo que antes flotaba en tooltips y en el «?» con globito de 320 px.
 * Regla de Joe: CERO tooltips; la ayuda va dentro de la página (2026-08-02,
 * reconfirmada 2026-09-07, aplicada a ArkTickets el 2026-10-13):
 *   · «Qué hace cada botón» → lo que traían los tooltips de los botones,
 *     incluido POR QUÉ a veces sale gris.
 *   · «Qué significa cada indicador» → chips, colores, etiquetas y columnas.
 * Los campos de formulario NO van aquí: llevan su texto en línea bajo el campo
 * (los switches, con `hint` de `ui/Toggle`).
 *
 * Es el mismo patrón que `ViewHelp` de fwserver, para que las dos apps se usen
 * igual. El manual largo con capturas sigue en `/ayuda`; `manual` enlaza a su
 * sección y la abre en otra pestaña: desde un modal de trabajo, navegar en la
 * misma pestaña tiraría el borrador.
 */

export type ItemAyuda = { label: ReactNode; text: ReactNode };

export type SeccionAyuda = {
  titulo: string;
  /** Pares etiqueta → explicación (botones, indicadores, columnas). */
  items?: ItemAyuda[];
  /** Texto libre, para lo que no es una lista. */
  cuerpo?: ReactNode;
};

export type ContenidoAyuda = {
  /** Para qué sirve la vista, en una o dos frases. */
  intro?: ReactNode;
  secciones: SeccionAyuda[];
  /** `id` de la sección del manual (`/ayuda#<id>`). `false` quita el pie
   *  (el Login: sin sesión, `/ayuda` te regresa al login). */
  manual?: string | false;
};

export default function AyudaVista({
  titulo,
  intro,
  secciones,
  manual,
  className,
}: ContenidoAyuda & {
  /** Nombre de la vista; la ventana se titula «Ayuda — <titulo>». */
  titulo: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // Una sección vacía (p. ej. sin botones para este rol) no se pinta: nada de
  // encabezados huérfanos.
  const visibles = secciones.filter((s) => s.cuerpo || (s.items && s.items.length > 0));
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`btn shrink-0 gap-1.5 border border-ink-600 bg-ink-800 px-2.5 text-xs text-zinc-300
                    hover:border-accent-500 hover:text-accent-300 focus-visible:outline-none
                    focus-visible:ring-2 focus-visible:ring-accent-500 ${className ?? ""}`}
      >
        <IconoAyuda className="h-4 w-4" />
        Ayuda
      </button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={`Ayuda — ${titulo}`}
        size="md"
        footer={
          <button type="button" className="btn-primary w-full sm:w-auto" onClick={() => setOpen(false)}>
            Cerrar
          </button>
        }
      >
        <div className="space-y-6 text-sm">
          {intro && <div className="space-y-2 leading-relaxed text-zinc-300">{intro}</div>}
          {visibles.map((s) => (
            <section key={s.titulo} className="space-y-2">
              <h3 className="font-semibold text-accent-300">{s.titulo}</h3>
              {s.cuerpo && <div className="space-y-2 leading-relaxed text-zinc-400">{s.cuerpo}</div>}
              {s.items && s.items.length > 0 && <ListaAyuda items={s.items} />}
            </section>
          ))}
          {manual !== false && (
          <p className="border-t border-ink-700 pt-3 text-xs text-zinc-500">
            {manual ? (
              <>
                Más detalle, con pantallas reales, en el{" "}
                <a
                  href={`/ayuda#${manual}`}
                  target="_blank"
                  rel="noopener"
                  className="text-accent-400 hover:underline"
                >
                  manual completo
                </a>{" "}
                (se abre en otra pestaña).
              </>
            ) : (
              <>
                El manual completo, con pantallas reales, está en{" "}
                <a href="/ayuda" target="_blank" rel="noopener" className="text-accent-400 hover:underline">
                  Centro de ayuda
                </a>{" "}
                (se abre en otra pestaña).
              </>
            )}
          </p>
          )}
        </div>
      </Modal>
    </>
  );
}

/**
 * Lista etiqueta → explicación. En el celular va apilada (etiqueta arriba);
 * de `sm` en adelante, en dos columnas con la etiqueta a la izquierda.
 */
export function ListaAyuda({ items }: { items: ItemAyuda[] }) {
  return (
    <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[minmax(8rem,14rem)_1fr] sm:gap-y-2.5">
      {items.map((it, i) => (
        <Fragment key={i}>
          <dt className="pt-2 font-medium text-zinc-100 first:pt-0 sm:pt-0">{it.label}</dt>
          <dd className="leading-relaxed text-zinc-400">{it.text}</dd>
        </Fragment>
      ))}
    </dl>
  );
}
